import { useRef, useState } from "react";
import type { ForensicRecoveryPath, ForensicRecoveryStatus } from "@shared/forensic-recovery-status";
import { canonicaliseRecoveryCode } from "@shared/lib/security-recovery/recovery-code-format";
import { AdaptiveDialog } from "@/components/ui/adaptive-dialog";
import { Button } from "@/components/ui/button";
import { ForensicRecoveryLiftGatePanel } from "@/components/forensic-recovery-lift-gate-panel";
import { ForensicRecoveryLiftQuestions } from "@/components/forensic-recovery-lift-questions";
import { formatTimestamp, safeParseDate } from "@/lib/format-timestamp";
import type { WardLiftChallenge, WardLiftQuestionsResponse } from "@/lib/forensic-recovery-lift-challenge";
import { isPotentiallyGatedPath, METHOD_COPY, stepLabel, TIME_COPY } from "@/components/forensic-recovery-dialog-copy";

interface ForensicRecoveryDialogProps {
  open: boolean;
  status: ForensicRecoveryStatus;
  error?: string | null;
  busy?: boolean;
  /**
   * The ward's own read of whether this incident's lift ceremony is
   * question-gated (#3042 Part B). Omitted (or `gated: false`), every path
   * below behaves exactly as it did before #3042 -- `onStartPath` and
   * `onRetryGuardian` fire with a single argument, byte-identical to the
   * pre-#3042 call shape (lock test in forensic-recovery-dialog.test.tsx).
   */
  liftQuestions?: WardLiftQuestionsResponse;
  /**
   * True while the `liftQuestions` read above is still in flight
   * (adversarial review round 1, BLOCK 2; split from error round 2, MAJOR
   * 2). Unknown is NOT the same as not-gated: a potentially-gated path
   * selection while this is true must hard-block -- the read may still
   * resolve to gated, so there is nothing safe to fall through to yet.
   */
  liftQuestionsPending?: boolean;
  /**
   * True while the `liftQuestions` read above has errored (round 2, MAJOR
   * 2, split out of the combined `liftQuestionsUnknown`). Also blocks a
   * potentially-gated path selection, but -- unlike pending -- offers an
   * explicit "Continue without questions" escape hatch: an unresolvable
   * read (e.g. a 500) must not hard-block guardian_quorum/key_guardian
   * forever, and blind "Try again" retries alone would burn the read
   * route's own rate budget.
   *
   * Round 3, Major: single-use per dialog open -- a second click when the
   * path is really gated would burn another of the ward's 3 lift attempts
   * (owner ruling 2026-09-15).
   */
  liftQuestionsErrored?: boolean;
  /** True while the `liftQuestions` read is in flight due to a retry, passed to the gate panel to disable "Try again" mid-flight. */
  liftQuestionsFetching?: boolean;
  /** Retries the `liftQuestions` read (e.g. `queryClient`'s `refetch`). Required to act on the blocked state. */
  onRetryLiftQuestions?: () => void;
  /** Builds the shared-nonce answer map (`prepareWardLiftChallenge`). Required to act on a gated path. */
  onBuildChallenge?: (rawAnswersByConnection: Record<string, string[]>) => Promise<WardLiftChallenge>;
  onOpenChange: (open: boolean) => void;
  onStartPath: (path: ForensicRecoveryPath, challenge?: WardLiftChallenge) => void;
  onCancel: (attemptId: string) => void;
  onContinue: (attemptId: string) => void;
  onSubmitCode: (attemptId: string, code: string) => void;
  onVeto: (attemptId: string) => void;
  onRetryGuardian: (attemptId: string, challenge?: WardLiftChallenge) => void;
}

type PendingLift =
  | { kind: "start"; path: ForensicRecoveryPath }
  | { kind: "retry"; attemptId: string };

/** Presents the current incident's server-approved recovery choices or progress. */
export function ForensicRecoveryDialog({
  open,
  status,
  error = null,
  busy = false,
  liftQuestions,
  liftQuestionsPending = false,
  liftQuestionsErrored = false,
  liftQuestionsFetching = false,
  onRetryLiftQuestions,
  onBuildChallenge,
  onOpenChange,
  onStartPath,
  onCancel,
  onContinue,
  onSubmitCode,
  onVeto,
  onRetryGuardian,
}: ForensicRecoveryDialogProps): React.ReactElement {
  const codeRef = useRef<HTMLInputElement>(null);
  const [pendingChallenge, setPendingChallenge] = useState<PendingLift | null>(null);
  const [blockedLift, setBlockedLift] = useState<PendingLift | null>(null);
  // Round 3, Major: single-use per dialog open -- once the ward has clicked
  // "Continue without questions", withhold the hatch for the rest of this
  // dialog open so a repeat click can't keep burning guardian-quorum lift
  // attempts against a path that really is gated (owner ruling 2026-09-15).
  const [escapeUsed, setEscapeUsed] = useState(false);
  // Round 2, MAJOR 2: internal name kept for the existing blocking logic --
  // pending or errored, either way the ward's own read hasn't confirmed
  // not-gated, so a potentially-gated path selection must not fall through.
  const liftQuestionsUnknown = liftQuestionsPending || liftQuestionsErrored;
  // Round 2, MINOR 3: no auto-replay. When the gating read resolves while a
  // selection is blocked, clear the block and return to the chooser instead
  // of silently re-deciding the ward's earlier click through the (now
  // different) gated/ungated branching -- the ward clicks their method again
  // explicitly. Adjusting state during render on a prop transition, per
  // React's "you might not need an effect" pattern, rather than a useEffect.
  const [prevLiftQuestionsUnknown, setPrevLiftQuestionsUnknown] = useState(liftQuestionsUnknown);
  if (liftQuestionsUnknown !== prevLiftQuestionsUnknown) {
    setPrevLiftQuestionsUnknown(liftQuestionsUnknown);
    if (!liftQuestionsUnknown && blockedLift) setBlockedLift(null);
  }
  const attempt = status.attempt;
  const title = attempt ? "Recovery in progress" : "Choose a recovery method";
  const holdUntil = attempt?.holdUntil ? safeParseDate(attempt.holdUntil) : null;

  function isGatedPath(path: ForensicRecoveryPath): boolean {
    return Boolean(liftQuestions?.gated && onBuildChallenge) && isPotentiallyGatedPath(path);
  }

  function handleStartPath(path: ForensicRecoveryPath): void {
    if (isPotentiallyGatedPath(path) && liftQuestionsUnknown) { setBlockedLift({ kind: "start", path }); return; }
    if (isGatedPath(path)) { setPendingChallenge({ kind: "start", path }); return; }
    onStartPath(path);
  }

  function handleRetryGuardian(attemptId: string): void {
    if (liftQuestionsUnknown) { setBlockedLift({ kind: "retry", attemptId }); return; }
    if (liftQuestions?.gated && onBuildChallenge) { setPendingChallenge({ kind: "retry", attemptId }); return; }
    onRetryGuardian(attemptId);
  }

  // Round 2, MAJOR 2: explicit escape hatch for an ERRORED (not merely
  // pending) gating read. Calls the pre-#3042 ungated shape -- a single
  // argument, byte-identical to the AC-1 ungated body -- so this can never
  // silently supply a stale/empty challenge.
  function handleContinueWithoutQuestions(): void {
    if (!blockedLift) return;
    if (blockedLift.kind === "start") onStartPath(blockedLift.path);
    else onRetryGuardian(blockedLift.attemptId);
    setBlockedLift(null);
    setEscapeUsed(true);
  }

  function handleChallengeSubmit(challenge: WardLiftChallenge): void {
    if (!pendingChallenge) return;
    if (pendingChallenge.kind === "start") onStartPath(pendingChallenge.path, challenge);
    else onRetryGuardian(pendingChallenge.attemptId, challenge);
    setPendingChallenge(null);
  }

  function handleDialogOpenChange(nextOpen: boolean): void {
    if (!nextOpen) { setPendingChallenge(null); setBlockedLift(null); setEscapeUsed(false); }
    onOpenChange(nextOpen);
  }

  return (
    <AdaptiveDialog
      open={open}
      onOpenChange={handleDialogOpenChange}
      title={title}
      description={attempt
        ? "Follow these steps in order. Your account stays paused until recovery finishes."
        : "Choose only from methods prepared before this incident and still safe to use."}
      size="lg"
      layer="recovery"
    >
      {error ? <p role="alert" aria-live="assertive" className="mb-4 text-sm text-warning">{error}</p> : null}

      {blockedLift ? (
        <ForensicRecoveryLiftGatePanel
          busy={liftQuestionsFetching}
          onRetry={onRetryLiftQuestions}
          onContinueWithoutQuestions={liftQuestionsErrored && !escapeUsed ? handleContinueWithoutQuestions : undefined}
          escapeUsed={escapeUsed}
          onChooseDifferentMethod={() => setBlockedLift(null)}
        />
      ) : pendingChallenge && liftQuestions && onBuildChallenge ? (
        <ForensicRecoveryLiftQuestions
          guardians={liftQuestions.guardians}
          busy={busy}
          onBuildChallenge={onBuildChallenge}
          onSubmit={handleChallengeSubmit}
          onChooseDifferentMethod={() => setPendingChallenge(null)}
        />
      ) : attempt ? (
        <div>
          <ol aria-label="Recovery steps" className="border-y border-border-default divide-y divide-border-default">
            {attempt.steps.map((step, index) => (
              <li key={`${step.key}-${index}`} className="flex items-center gap-3 py-3 text-sm">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-border-default text-text-secondary" aria-hidden="true">
                  {index + 1}
                </span>
                <span className={step.state === "complete" ? "text-text-secondary" : "text-text-primary"}>
                  {stepLabel(step.key, index)}{step.state === "complete" ? " complete" : ""}
                </span>
              </li>
            ))}
          </ol>

          <div role="status" aria-live="polite" className="mt-4 text-sm text-text-secondary">
            {attempt.notificationState === "unavailable"
              ? "Notifications are not available for this attempt. Cancel it and choose another method."
              : attempt.notificationState === "pending" && attempt.status === "holding"
                ? "Security notifications are being prepared. This recovery stays paused while that completes."
                : attempt.notificationState === "queued" && attempt.status === "holding" && holdUntil
                  ? `Security notifications are queued. The safety hold ends ${formatTimestamp(holdUntil)}.`
                : attempt.status === "ready"
                  ? "The safety hold is complete. Finish recovery to sign in again."
                  : "Your completed checks appear here as recovery continues."}
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            {attempt.nextAction === "offline_code" ? (
              <form className="w-full space-y-3" onSubmit={(event) => {
                event.preventDefault();
                const typed = codeRef.current?.value.trim() ?? "";
                if (!typed) return;
                if (codeRef.current) codeRef.current.value = "";
                // Send the canonical form so any grouping, case, or stray
                // whitespace off a paper copy submits identically. The server
                // canonicalises again and never trusts this.
                onSubmitCode(attempt.attemptId, canonicaliseRecoveryCode(typed) ?? typed);
              }}>
                <label className="block text-sm font-medium text-text-primary" htmlFor="forensic-offline-code">
                  Offline recovery code
                </label>
                <input ref={codeRef} id="forensic-offline-code" autoComplete="off" spellCheck={false}
                  className="min-h-[44px] w-full rounded-[var(--radius-md)] border border-border-default bg-bg-primary px-3 text-text-primary" />
                <Button type="submit" variant="primaryAccessible" loading={busy}>Use offline code</Button>
              </form>
            ) : attempt.nextAction === "retry_guardian" ? (
              <Button type="button" variant="primaryAccessible" loading={busy}
                onClick={() => handleRetryGuardian(attempt.attemptId)}>Ask a trusted person</Button>
            ) : attempt.nextAction === "passkey_1" || attempt.nextAction === "passkey_2"
              || attempt.nextAction === "lift" ? (
              <Button type="button" variant="primaryAccessible" loading={busy} onClick={() => onContinue(attempt.attemptId)}>
                {attempt.nextAction === "lift" ? "Finish recovery"
                  : attempt.nextAction === "passkey_2" ? "Verify another security device" : "Verify security device"}
              </Button>
            ) : null}
            {attempt.canCancel ? (
              <Button type="button" variant="secondary" disabled={busy} onClick={() => onCancel(attempt.attemptId)}>
                Cancel recovery
              </Button>
            ) : null}
            {attempt.canVeto ? (
              <Button type="button" variant="secondary" disabled={busy}
                onClick={() => onVeto(attempt.attemptId)}>Stop this recovery</Button>
            ) : null}
          </div>
        </div>
      ) : status.eligiblePaths.length > 0 ? (
        <div>
          <ul className="border-y border-border-default divide-y divide-border-default">
          {status.eligiblePaths.map((path) => {
            const copy = METHOD_COPY[path.key];
            return (
              <li key={path.key} className="py-5">
                <h3 className="font-semibold text-text-primary">{copy.title}</h3>
                <dl className="mt-3 space-y-2 text-sm">
                  <div><dt className="font-medium text-text-primary">Why it helps</dt><dd className="text-text-secondary">{copy.why}</dd></div>
                  <div><dt className="font-medium text-text-primary">Time</dt><dd className="text-text-secondary">{TIME_COPY[path.time]}</dd></div>
                  <div><dt className="font-medium text-text-primary">Another person</dt><dd className="text-text-secondary">{path.anotherPerson ? "Yes" : "No"}</dd></div>
                </dl>
                <Button type="button" variant="secondary" className="mt-4 min-h-[44px]" disabled={busy} onClick={() => handleStartPath(path.key)}>
                  {copy.action}
                </Button>
              </li>
            );
          })}
          </ul>
        </div>
      ) : (
        <p role="status" className="border-y border-border-default py-5 text-sm text-text-secondary">
          No recovery method is available for this incident. Keep the account paused and contact the platform operator.
        </p>
      )}
    </AdaptiveDialog>
  );
}
